/**
 * security/SecureStorage.js
 * 
 * Centralized utility for handling sensitive data storage securely on the client side.
 * Prevents data leakage by avoiding insecure document.cookies for PII (Personally Identifiable Information).
 * 
 * Future security implementations (encryption, secure session handling, strict token management)
 * should be added to this module.
 */

export const SecureStorage = {
    /**
     * Store temporary sensitive data in SessionStorage
     * Data is cleared when the page session ends (e.g. tab closed).
     * Prefer this over LocalStorage or Cookies for PII like Emails/Phone numbers during flows.
     */
    setSessionData: (key, value) => {
        try {
            const serializedValue = JSON.stringify(value);
            sessionStorage.setItem(key, serializedValue);
        } catch (error) {
            console.error(`SecureStorage Error: Could not save ${key} to session state.`, error);
        }
    },

    /**
     * Retrieve temporary sensitive data from SessionStorage
     */
    getSessionData: (key) => {
        try {
            const item = sessionStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            console.error(`SecureStorage Error: Could not parse ${key} from session state.`, error);
            return null;
        }
    },

    /**
     * Remove specific sensitive data from SessionStorage
     */
    removeSessionData: (key) => {
        try {
            sessionStorage.removeItem(key);
        } catch (error) {
            console.error(`SecureStorage Error: Could not remove ${key} from session state.`, error);
        }
    },

    /**
     * Clear all sensitive data tightly bound to the current session flow (e.g. on logout or completion)
     */
    clearAllSessionData: () => {
        try {
            sessionStorage.clear();
        } catch (error) {
            console.error('SecureStorage Error: Could not clear session state.', error);
        }
    },

    /**
     * Note on Auth Tokens:
     * Access Tokens should ideally NOT be stored in LocalStorage or standard JavaScript accessible Cookies.
     * The safest approach is HttpOnly, Secure, SameSite cookies set directly by the Backend API response.
     * 
     * If you MUST handle tokens via JavaScript, minimize their exposure window.
     */
    setAuthTokenInsecurely: (token, expirationDays = 30) => {
        // WARNING: This is vulnerable to XSS. This wrapper exists to flag insecure usage 
        // until the backend is updated to send HttpOnly cookies.
        console.warn('SECURITY WARNING: Storing auth tokens via JavaScript is vulnerable to XSS.');
        const date = new Date();
        date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
        // Add Secure and SameSite=Strict flags to baseline cookie security against CSRF and sniffing
        document.cookie = `token=${token};expires=${date.toUTCString()};path=/;Secure;SameSite=Strict`;
    }
};
